import { useCallback, useEffect, useRef, useState } from "react";
import type { DowntimeReasonProposal, DowntimeReasonProposalDecisionInput } from "../../../shared/production/downtime-reason-proposals";
import { productionGateway } from "./production-gateway";

export type DowntimeReasonProposalDraft = { groupName: string; planned: boolean; note: string };
const emptyDraft = (): DowntimeReasonProposalDraft => ({ groupName: "", planned: false, note: "" });

export function useDowntimeReasonProposals() {
  const [items, setItems] = useState<DowntimeReasonProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, DowntimeReasonProposalDraft>>({});
  const live = useRef(true);
  const request = useRef(0);
  const reload = useCallback(async () => {
    const sequence = ++request.current;
    setLoading(true); setError(null);
    try {
      const result = await productionGateway.downtimeReasonProposals();
      if (!live.current || sequence !== request.current) return;
      setItems(result.items.filter((item) => item.status === "PENDING"));
    } catch (failure) {
      if (live.current && sequence === request.current) setError(failure instanceof Error ? failure.message : "Öneriler alınamadı.");
    } finally {
      if (live.current && sequence === request.current) { setLoading(false); setLoaded(true); }
    }
  }, []);
  useEffect(() => { live.current = true; void reload(); return () => { live.current = false; request.current += 1; }; }, [reload]);
  const draftFor = useCallback((item: DowntimeReasonProposal) => drafts[item.id] ?? emptyDraft(), [drafts]);
  const change = useCallback((item: DowntimeReasonProposal, next: Partial<DowntimeReasonProposalDraft>) => {
    setDrafts((current) => ({ ...current, [item.id]: { ...(current[item.id] ?? emptyDraft()), ...next } }));
  }, []);
  const decide = useCallback(async (item: DowntimeReasonProposal, decision: "APPROVED" | "REJECTED") => {
    if (savingId) return;
    const draft = drafts[item.id] ?? emptyDraft();
    const input: DowntimeReasonProposalDecisionInput = decision === "APPROVED"
      ? { decision, groupName: draft.groupName.trim(), planned: draft.planned, ...(draft.note.trim() ? { note: draft.note.trim() } : {}) }
      : { decision, ...(draft.note.trim() ? { note: draft.note.trim() } : {}) };
    if (decision === "APPROVED" && draft.groupName.trim().length < 3) { setError("Onay için geçerli bir grup adı girin."); return; }
    setSavingId(item.id); setError(null);
    try {
      await productionGateway.decideDowntimeReasonProposal(item.id, input);
      if (!live.current) return;
      setItems((current) => current.filter((candidate) => candidate.id !== item.id));
      setDrafts((current) => { const next = { ...current }; delete next[item.id]; return next; });
    } catch (failure) {
      if (live.current) setError(failure instanceof Error ? failure.message : "Karar kaydedilemedi; öneri listede korunuyor.");
    } finally { if (live.current) setSavingId(null); }
  }, [drafts, savingId]);
  return { items, loading, loaded, error, savingId, draftFor, change, reload, decide };
}
